import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { products } from '../data/products';
import stackntrackLogo from '../assets/logos/Group 80.png';
import evologLogo from '../assets/logos/applogo_full 2.png';
import slidewithaiLogo from '../assets/logos/Group 83.png';

const logos: Record<string, string> = {
  stackntrack: stackntrackLogo,
  evolog: evologLogo,
  slidewithai: slidewithaiLogo,
};

const darkBg = { background: 'linear-gradient(180deg, rgba(28,13,34,1) 0%, rgba(7,0,13,1) 100%)' };

const ProductsShowcase: React.FC = () => (
  <div>
    {/* Hero — dark with tonal gradient */}
    <section className="relative pt-40 pb-32 px-8 overflow-hidden" style={darkBg}>
      {/* Ambient glow */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#7F03A1]/20 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10 text-center">
        <span className="text-sm font-semibold uppercase tracking-[0.15em] text-purple-400/60 mb-6 block">
          Portfolio / What We've Built
        </span>
        <h1 className="text-5xl md:text-7xl font-bold tracking-[-0.03em] leading-tight mb-8 max-w-4xl mx-auto text-white">
          Real products solving real problems,{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-fuchsia-300">
            built with the same frameworks
          </span>{' '}
          we teach.
        </h1>
        <div className="w-24 h-1 bg-[#7F03A1] rounded-full mx-auto" />
      </div>
    </section>

    {/* Product Sections — alternating white / light */}
    {products.map((product, i) => {
      const isWhite = i % 2 === 0;
      const imageLeft = i % 2 === 0;
      const bgClass = isWhite ? 'bg-white' : 'bg-[#faf8fc]';
      const pillBg = isWhite
        ? 'bg-gray-100 text-gray-600'
        : 'bg-white text-gray-600 shadow-sm';

      const imageBlock = (
        <div className={`relative ${imageLeft ? 'order-2 lg:order-1' : ''}`}>
          {/* Rotated accent background */}
          <div className={`absolute -inset-4 bg-[#7F03A1]/5 rounded-xl ${imageLeft ? '-rotate-2' : 'rotate-2'}`} />
          {/* Screenshot placeholder */}
          <a href={product.url} target="_blank" rel="noopener noreferrer" className="block relative rounded-lg shadow-2xl w-full aspect-[16/10] bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-100 overflow-hidden flex items-center justify-center p-10 group cursor-pointer">
            <img src={logos[product.id]} alt={product.name} className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-gray-900 text-sm font-semibold px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                Visit site
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </span>
            </div>
          </a>
        </div>
      );

      const contentBlock = (
        <div className={imageLeft ? 'order-1 lg:order-2' : ''}>
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#7F03A1] mb-4 block">
            {product.id === 'stackntrack' ? 'Gamified Habits' : product.id === 'evolog' ? 'Decision Intelligence' : 'Content Creation'}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
            {product.name}
          </h2>
          <p className="text-lg text-gray-500 mb-8 leading-relaxed">
            {product.description}
          </p>
          <ul className="space-y-4 mb-10">
            {product.highlights.slice(0, 2).map((highlight, j) => (
              <li key={j} className="flex items-center gap-3 text-gray-700 font-medium">
                <svg className="w-6 h-6 text-[#7F03A1] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
                {highlight}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 mb-8">
            {product.techPills.map(pill => (
              <span
                key={pill}
                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${pillBg}`}
              >
                {pill}
              </span>
            ))}
          </div>
          <a
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#7F03A1] hover:text-[#9a04c4] transition-colors group"
          >
            Visit {product.name}
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      );

      return (
        <section key={product.id} className={`${bgClass} py-32 px-8 overflow-hidden`}>
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            {imageLeft ? (
              <>{imageBlock}{contentBlock}</>
            ) : (
              <>{contentBlock}{imageBlock}</>
            )}
          </div>
        </section>
      );
    })}

    {/* CTA Section — dark */}
    <section
      className="py-32 px-8 text-center relative overflow-hidden"
      style={{ background: '#17081c' }}
    >
      {/* Bottom gradient glow */}
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-[#7F03A1]/10 to-transparent pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-8 text-white">
          Ready to begin your building journey?
        </h2>
        <p className="text-xl text-white/40 mb-12 max-w-2xl mx-auto">
          The masterclass is where it starts. Learn the mindset, the framework, and how to think like a builder in the age of AI.
        </p>
        <Link to="/masterclass">
          <Button size="lg" className="rounded-full font-bold shadow-2xl shadow-purple-600/20 group">
            Join the Masterclass
            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Button>
        </Link>
      </div>
    </section>
  </div>
);

export default ProductsShowcase;
